import React, { createContext, useContext, useState, useEffect } from 'react'
import { authAPI } from '../utils/api'
import logger from '../utils/logger'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') {
      setLoading(false)
      return
    }

    // Check for stored token on app load
    const storedToken = localStorage.getItem('surviveit_token')
    const storedUser = localStorage.getItem('surviveit_user')
    
    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser)
        setToken(storedToken)
        setUser(parsedUser)
        
        // Set user context in logger
        logger.setUser(parsedUser)
        logger.info('User auto-logged in from stored credentials', {
          userId: parsedUser.id,
          username: parsedUser.username
        })
      } catch (err) {
        logger.error('Error parsing stored user data', { error: err.message })
        localStorage.removeItem('surviveit_token')
        localStorage.removeItem('surviveit_user')
      }
    }
    setLoading(false)
  }, [])

  const login = async (username, password) => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await authAPI.login({ email: username, password })
      
      setUser(response.user)
      setToken(response.token)
      
      // Store in localStorage (browser only)
      if (typeof window !== 'undefined') {
        localStorage.setItem('surviveit_token', response.token)
        localStorage.setItem('surviveit_user', JSON.stringify(response.user))
      }
      
      // Set user context in logger
      logger.setUser(response.user)
      logger.userAction('user_login', {
        userId: response.user.id,
        username: response.user.username
      })
      
      return response
    } catch (err) {
      setError(err.message || 'Login failed')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const register = async (email, username, password) => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await authAPI.register({ email, username, password })
      
      setUser(response.user)
      setToken(response.token)
      
      // Store in localStorage (browser only)
      if (typeof window !== 'undefined') {
        localStorage.setItem('surviveit_token', response.token)
        localStorage.setItem('surviveit_user', JSON.stringify(response.user))
      }
      
      return response
    } catch (err) {
      setError(err.message || 'Registration failed')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    setUser(null)
    setToken(null)
    setError(null)
    
    // Clear from localStorage (browser only)
    if (typeof window !== 'undefined') {
      localStorage.removeItem('surviveit_token')
      localStorage.removeItem('surviveit_user')
    }
  }

  const isAuthenticated = () => {
    return !!user && !!token
  }

  const value = {
    user,
    token,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated,
    setError
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}